import { useCallback, useRef, useState, type KeyboardEvent } from 'react';

import { type SelectionOption } from '../types/selection-option.types';

/** The attribute a row carries so the hook can find it without holding a ref per row. */
export const SELECTION_ROW_ATTRIBUTE = 'data-selection-value';

export interface UseSelectionKeyboardInput {
  /** In render order. */
  options: readonly SelectionOption[];
  /**
   * Handle only the step that ENTERS the list, and leave movement inside it
   * alone.
   *
   * For the single-select panel, whose rows are a Radix `RadioGroup`: Radix
   * already owns arrow movement between radios, so handling it here too would
   * move the focus twice per press. What Radix does not do is walk in from the
   * filter input above it, which is the one step this covers.
   */
  isEntryOnly?: boolean;
  /**
   * The rows are laid out as a **grid** rather than as a column (the media
   * library's tiles).
   *
   * That changes two things and nothing else. The horizontal arrows start
   * meaning something — one tile, MIRRORED in Arabic — and the vertical ones
   * move a whole row instead of one item. The column count is deliberately not a
   * parameter: the grid is `auto-fill`, so CSS decides how many fit from the
   * container's width, and any number passed in here would be wrong at some
   * viewport. It is measured from the laid-out rows instead.
   *
   * Vertical movement also stops at the edges rather than wrapping. Wrapping is
   * right for a list, where "past the end" is unambiguous; in a grid it lands
   * the operator in an arbitrary column and reads as the focus jumping at random.
   */
  isGrid?: boolean;
}

export interface UseSelectionKeyboard {
  /** The one row carrying `tabIndex={0}`. */
  activeValue: string | null;
  /**
   * Attach to the element that wraps the rows.
   *
   * A CALLBACK ref, not a ref object: React Compiler's `refs` rule is on at
   * error level and a ref object travelling through props is read during render
   * wherever it lands. `useIntersection` returns a callback ref for the same
   * reason, and it is also strictly more correct — React invokes it the moment
   * the node mounts.
   */
  listRef: (node: HTMLDivElement | null) => void;
  /** Attach to the filter input AND to the row container. */
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/**
 * How many tiles share the first row — the grid's column count, MEASURED.
 *
 * `auto-fill` publishes no count anywhere in the DOM, so the only honest source
 * is the layout itself: the tiles whose `offsetTop` matches the first one's are
 * its row. Read at keypress time rather than cached, because a resize changes
 * the answer and nothing notifies this hook.
 *
 * Falls back to 1, which degrades the grid to the list behaviour rather than
 * dividing by zero — the case where the container has not been laid out yet.
 */
function measureColumns(list: HTMLElement | null): number {
  if (list === null) {
    return 1;
  }

  const tiles = [...list.querySelectorAll<HTMLElement>(`[${SELECTION_ROW_ATTRIBUTE}]`)];
  const first = tiles[0];

  if (first === undefined) {
    return 1;
  }

  const columns = tiles.filter((tile) => tile.offsetTop === first.offsetTop).length;

  return Math.max(1, columns);
}

/**
 * A roving tab stop over a checkbox list — or, with `isGrid`, over a grid.
 *
 * A checkbox group is a single tab stop by convention, and this is what makes it
 * one: without it, forty options are forty presses of Tab to get past. Radix's
 * `RadioGroup` gives the single-select panel the same behaviour natively, so
 * only the MULTI list needs this.
 *
 * The handler sits on the whole panel rather than on the list, so ArrowDown from
 * the filter input walks into the options — the combobox gesture an operator
 * already has in their fingers. `Home`/`End` are deliberately NOT captured while
 * focus is in that input, where they mean caret movement and taking them would
 * be a regression in a text field.
 *
 * Rows are found by attribute rather than by a ref per row: a per-row callback
 * ref is a new function identity on every render, which detaches and re-attaches
 * every node on every keystroke of the filter above them.
 *
 * **Direction matters in a GRID and nowhere else.** A vertical list has no
 * horizontal axis, so it takes neither arrow and has no RTL branch to get wrong.
 * A grid takes both, and "next" is to the LEFT in Arabic — so `isGrid` is the
 * one mode with a direction read in it, and it takes that from the resolved
 * `direction` of the row container rather than from a locale (U7). Said plainly
 * because this comment used to claim the whole hook was direction-agnostic,
 * which stopped being true the moment the grid arrived.
 */
export function useSelectionKeyboard({
  options,
  isEntryOnly = false,
  isGrid = false,
}: UseSelectionKeyboardInput): UseSelectionKeyboard {
  const listNodeRef = useRef<HTMLDivElement | null>(null);
  const [preferred, setPreferred] = useState<string | null>(null);

  const listRef = useCallback((node: HTMLDivElement | null) => {
    listNodeRef.current = node;
  }, []);

  const selectable = options.filter((option) => option.isDisabled !== true);

  // Derived, never an effect: when the filter narrows the list out from under
  // the stop, the next render simply falls back to the first selectable row.
  const activeValue =
    preferred !== null && selectable.some((option) => option.value === preferred)
      ? preferred
      : (selectable[0]?.value ?? null);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (selectable.length === 0) return;

      const isInTextField = event.target instanceof HTMLInputElement;
      // Entering the list is not the same gesture as moving inside it: the first
      // arrow from the filter lands ON the roving stop rather than one past it,
      // so the first row is reachable and the stop is where it was left.
      const isEntering =
        !(event.target instanceof Node) || listNodeRef.current?.contains(event.target) !== true;
      if (isEntryOnly && !isEntering) return;

      const currentIndex = selectable.findIndex((option) => option.value === activeValue);
      const last = selectable.length - 1;

      // A whole row in a grid, one item in a list. Measured at press time: the
      // container's width decides it and a resize changes it silently.
      const step = isGrid ? measureColumns(listNodeRef.current) : 1;

      /**
       * Vertical movement. A list WRAPS (past the end is unambiguously the
       * start); a grid CLAMPS, because wrapping there lands the operator in an
       * arbitrary column and reads as the focus jumping at random.
       */
      const vertical = (delta: number): number => {
        if (isEntering) return Math.max(currentIndex, 0);
        if (currentIndex === -1) return delta > 0 ? 0 : last;

        const target = currentIndex + delta * step;

        return isGrid
          ? target < 0 || target > last
            ? currentIndex
            : target
          : (target + selectable.length) % selectable.length;
      };

      let nextIndex: number | null = null;
      switch (event.key) {
        case 'ArrowDown':
          nextIndex = vertical(1);
          break;
        case 'ArrowUp':
          nextIndex = vertical(-1);
          break;
        // Horizontal arrows exist only in a grid — a vertical list has no
        // horizontal axis, and taking those keys there would steal a gesture the
        // browser or a nested control may want.
        case 'ArrowRight':
        case 'ArrowLeft': {
          if (!isGrid || isEntering) break;

          // MIRRORED: in Arabic the next tile is to the LEFT. Read from the
          // resolved direction rather than from a locale, so a `dir` set
          // anywhere above this list is honoured (U7).
          const isRtl =
            listNodeRef.current !== null &&
            getComputedStyle(listNodeRef.current).direction === 'rtl';
          const forward = (event.key === 'ArrowRight') !== isRtl;
          const target = currentIndex + (forward ? 1 : -1);

          if (target >= 0 && target <= last) nextIndex = target;
          break;
        }
        case 'Home':
          if (!isInTextField) nextIndex = 0;
          break;
        case 'End':
          if (!isInTextField) nextIndex = last;
          break;
        default:
          break;
      }

      if (nextIndex === null) return;

      const nextValue = selectable[nextIndex]?.value;
      if (nextValue === undefined) return;

      event.preventDefault();
      setPreferred(nextValue);

      const node = listNodeRef.current?.querySelector<HTMLElement>(
        `[${SELECTION_ROW_ATTRIBUTE}="${CSS.escape(nextValue)}"]`,
      );
      node?.focus();
    },
    [activeValue, isEntryOnly, isGrid, selectable],
  );

  return { activeValue, listRef, onKeyDown };
}
